import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const DAY_MAP = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
  thursday: 4, friday: 5, saturday: 6
};

const DAY_NAMES_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const TZ_SHORT = {
  'America/New_York': 'ET',
  'America/Chicago': 'CT',
  'America/Denver': 'MT',
  'America/Los_Angeles': 'PT',
  'Asia/Kolkata': 'IST'
};

const FREQ_SHORT = {
  monthly: '/mo',
  quarterly: '/quarter',
  semi_annual: '/6 mo',
  annual: '/yr',
  one_time: ' one-time',
  'one-time': ' one-time'
};

const getNextClassDate = (course) => {
  if (!course?.schedule?.days?.length) return null;
  if (course.type !== 'long_course' && course.type !== 'block') return null;

  const courseDays = course.schedule.days.map(d => DAY_MAP[d]);
  const now = new Date();
  const today = now.getDay();

  // Check today and next 7 days
  for (let offset = 0; offset < 7; offset++) {
    const checkDay = (today + offset) % 7;
    if (courseDays.includes(checkDay)) {
      const nextDate = new Date(now);
      nextDate.setDate(now.getDate() + offset);
      nextDate.setHours(0, 0, 0, 0);
      return nextDate;
    }
  }
  return null;
};

const formatNextClass = (course) => {
  const nextDate = getNextClassDate(course);
  if (!nextDate) return null;

  const now = new Date();
  const isToday = nextDate.toDateString() === now.toDateString();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow = nextDate.toDateString() === tomorrow.toDateString();

  const dayLabel = isToday ? 'Today' : isTomorrow ? 'Tomorrow' : nextDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

  const tz = TZ_SHORT[course.schedule?.timezone] || '';
  const time = course.schedule?.startTime ? `at ${course.schedule.startTime} ${tz}`.trim() : '';

  return { dayLabel, time, isToday, isTomorrow, date: nextDate };
};

const StudentOverview = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [materialsMap, setMaterialsMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await api.get('/enroll/my');
        const enrs = data.enrollments || [];
        setEnrollments(enrs);

        // Fetch materials for active enrollments
        const active = enrs.filter(e => e.status === 'active' && e.course);
        const materialsResults = await Promise.allSettled(
          active.map(e => api.get(`/materials/student/${e.course._id}`))
        );

        const mMap = {};
        active.forEach((e, i) => {
          if (materialsResults[i].status === 'fulfilled') {
            mMap[e.course._id] = materialsResults[i].value.data;
          }
        });
        setMaterialsMap(mMap);
      } catch (err) {
        console.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return <div className="text-gray-500">Loading dashboard...</div>;
  }

  const activeEnrollments = enrollments.filter(e => e.status === 'active' && e.course);
  const pastEnrollments = enrollments.filter(e => e.status !== 'active' && e.course);

  // Find the soonest next class across all courses
  let soonestClass = null;
  let soonestEnrollment = null;
  activeEnrollments.forEach(e => {
    const next = formatNextClass(e.course);
    if (next && (!soonestClass || next.date < soonestClass.date)) {
      soonestClass = next;
      soonestEnrollment = e;
    }
  });

  // Aggregate attendance for this month
  const totalAttended = activeEnrollments.reduce((sum, e) => sum + (e.totalClassesAttended || 0), 0);
  const totalMissed = activeEnrollments.reduce((sum, e) => sum + (e.totalClassesMissed || 0), 0);
  const totalClasses = totalAttended + totalMissed;
  const attendanceRate = totalClasses > 0 ? Math.round((totalAttended / totalClasses) * 100) : 0;

  // Recently unlocked materials (across all courses)
  const recentMaterials = [];
  const nextToUnlock = [];
  activeEnrollments.forEach(e => {
    const mData = materialsMap[e.course?._id];
    if (!mData?.materials) return;
    mData.materials.forEach(week => {
      if (week.unlocked) {
        recentMaterials.push({
          courseName: e.course.name,
          courseId: e.course._id,
          week: week.week,
          title: week.title || `Week ${week.week}`,
          videos: week.videos || []
        });
      } else if (nextToUnlock.length < 2) {
        const classesNeeded = week.week - (mData.attendanceCount || 0);
        if (classesNeeded > 0) {
          nextToUnlock.push({
            courseName: e.course.name,
            courseId: e.course._id,
            week: week.week,
            title: week.title || `Week ${week.week}`,
            classesNeeded
          });
        }
      }
    });
  });
  // Show only last 3 unlocked
  const recentUnlocked = recentMaterials.slice(-3).reverse();

  // Upcoming holidays (studio + US)
  const now = new Date();
  const year = now.getFullYear();
  const studioHols = [];
  activeEnrollments.forEach(e => {
    (e.studio?.holidays || []).forEach(h => {
      const d = new Date(h.date);
      if (d >= now) studioHols.push({ date: d, name: h.name, type: 'studio' });
    });
  });
  // US holidays
  const usHols = [
    { date: new Date(year, 0, 1), name: "New Year's Day" },
    { date: new Date(year, 0, 1 + ((1 - new Date(year, 0, 1).getDay() + 7) % 7) + 14), name: 'MLK Day' },
    { date: new Date(year, 4, 31 - ((new Date(year, 4, 31).getDay() + 6) % 7)), name: 'Memorial Day' },
    { date: new Date(year, 6, 4), name: 'Independence Day' },
    { date: new Date(year, 8, 1 + ((1 - new Date(year, 8, 1).getDay() + 7) % 7)), name: 'Labor Day' },
    { date: new Date(year, 10, 1 + ((4 - new Date(year, 10, 1).getDay() + 7) % 7) + 21), name: 'Thanksgiving' },
    { date: new Date(year, 11, 25), name: 'Christmas Day' },
  ].filter(h => h.date >= now).map(h => ({ ...h, type: 'us' }));

  const uniqueHolidays = [...new Map([...studioHols, ...usHols].map(h => [h.name, h])).values()]
    .sort((a, b) => a.date - b.date)
    .slice(0, 5);

  // Next payment date
  const nextPayment = activeEnrollments.find(e => e.currentPeriodEnd);
  const nextPaymentDate = nextPayment?.currentPeriodEnd ? new Date(nextPayment.currentPeriodEnd) : null;

  // Determine greeting based on new student vs returning
  const isNewStudent = totalClasses < 4;
  const firstName = user?.name?.split(' ')[0] || 'there';

  return (
    <div className="flex gap-8">
      {/* Main Column */}
      <div className="flex-1 min-w-0">
        {/* Welcome + Next Class */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            {isNewStudent ? `Welcome, ${firstName}!` : `Hey, ${firstName}`}
          </h1>
          {isNewStudent && (
            <p className="text-sm text-gray-500 mb-4">Here's everything you need to get started.</p>
          )}
        </div>

        {/* Next Class Card */}
        {soonestClass && soonestEnrollment && (
          <div className={`rounded-xl border shadow-sm p-5 mb-6 ${
            soonestClass.isToday ? 'bg-brand-50 border-brand-200' : 'bg-white border-gray-100'
          }`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs font-semibold text-brand-500 uppercase tracking-wide mb-1">
                  {soonestClass.isToday ? 'Class Today' : 'Next Class'}
                </div>
                <h3 className="text-lg font-bold text-gray-900">{soonestEnrollment.course.name}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {soonestClass.dayLabel} {soonestClass.time}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {soonestEnrollment.course.zoom?.joinUrl && (
                  <a
                    href={soonestEnrollment.course.zoom.joinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
                    </svg>
                    Join on Zoom
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Course Cards */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">My Courses</h2>
          {activeEnrollments.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
              <p className="text-gray-500">No active courses. Enroll in a course to get started.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {activeEnrollments.map(enrollment => {
                const course = enrollment.course;
                const mData = materialsMap[course._id];
                const unlockedCount = mData?.materials?.filter(m => m.unlocked).length || 0;
                const totalWeeks = mData?.totalWeeks || mData?.materials?.length || 0;
                const attended = enrollment.totalClassesAttended || 0;
                const missed = enrollment.totalClassesMissed || 0;
                const rate = (attended + missed) > 0 ? Math.round((attended / (attended + missed)) * 100) : 0;
                const tz = TZ_SHORT[course.schedule?.timezone] || '';
                const days = (course.schedule?.days || []).map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(', ');
                const scheduleStr = days ? `${days} ${course.schedule?.startTime || ''} ${tz}`.trim() : '';

                return (
                  <div
                    key={enrollment._id}
                    onClick={() => navigate(`/student/course/${course._id}`)}
                    className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all cursor-pointer p-4"
                  >
                    <h3 className="font-semibold text-gray-900 mb-1 truncate">{course.name}</h3>
                    {scheduleStr && (
                      <p className="text-xs text-gray-500 mb-3">{scheduleStr}</p>
                    )}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Attendance</span>
                        <span className="font-medium text-gray-900">{attended} classes ({rate}%)</span>
                      </div>
                      {totalWeeks > 0 && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">Materials</span>
                          <span className="font-medium text-gray-900">{unlockedCount}/{totalWeeks} unlocked</span>
                        </div>
                      )}
                    </div>
                    {/* Quick actions */}
                    <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-50">
                      {course.zoom?.joinUrl && (
                        <a
                          href={course.zoom.joinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          className="text-xs font-medium text-brand-500 hover:text-brand-600"
                        >
                          Join Zoom
                        </a>
                      )}
                      {course.googleClassroom?.courseUrl && (
                        <a
                          href={course.googleClassroom.courseUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          className="text-xs font-medium text-brand-500 hover:text-brand-600"
                        >
                          Classroom
                        </a>
                      )}
                      <button
                        onClick={e => { e.stopPropagation(); navigate(`/student/course/${course._id}`); }}
                        className="text-xs font-medium text-gray-500 hover:text-gray-700"
                      >
                        Materials
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recently Unlocked Materials */}
        {recentUnlocked.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Recently Unlocked</h2>
            <div className="space-y-2">
              {recentUnlocked.map((m, i) => (
                <div
                  key={i}
                  onClick={() => navigate(`/student/course/${m.courseId}`)}
                  className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:border-brand-200 hover:shadow-md transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-brand-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-brand-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">Week {m.week}: {m.title}</p>
                      <p className="text-xs text-gray-500">{m.courseName} {m.videos.length > 0 ? `\u00b7 ${m.videos.length} video${m.videos.length !== 1 ? 's' : ''}` : ''}</p>
                    </div>
                    <svg className="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>

            {/* Next to unlock */}
            {nextToUnlock.length > 0 && (
              <div className="mt-3 px-4 py-3 bg-gray-50 rounded-lg border border-gray-100">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Next to Unlock</p>
                {nextToUnlock.map((m, i) => (
                  <p key={i} className="text-sm text-gray-600">
                    <span className="font-medium">{m.title}</span>
                    <span className="text-gray-400"> — attend {m.classesNeeded} more class{m.classesNeeded !== 1 ? 'es' : ''}</span>
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Past Enrollments */}
        {pastEnrollments.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Past Courses</h2>
            <div className="space-y-2">
              {pastEnrollments.map(enrollment => (
                <div
                  key={enrollment._id}
                  onClick={() => navigate(`/student/course/${enrollment.course._id}`)}
                  className="bg-white rounded-lg border border-gray-100 p-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-700">{enrollment.course.name}</p>
                    <p className="text-xs text-gray-400">{enrollment.totalClassesAttended || 0} classes attended</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    enrollment.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                    enrollment.status === 'cancelled' ? 'bg-gray-100 text-gray-500' :
                    enrollment.status === 'payment_failed' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-500'
                  }`}>
                    {enrollment.status === 'completed' ? 'Completed' :
                     enrollment.status === 'cancelled' ? 'Cancelled' :
                     enrollment.status === 'payment_failed' ? 'Payment Failed' :
                     enrollment.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Right Sidebar */}
      <div className="w-72 flex-shrink-0 space-y-5 hidden lg:block">
        {/* To-Do */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">To Do</h3>
          <div className="space-y-3">
            {recentUnlocked.length > 0 ? (
              recentUnlocked.slice(0, 2).map((m, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded bg-brand-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-brand-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-700 leading-tight">Watch: {m.title}</p>
                    <p className="text-xs text-gray-400">{m.courseName}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-400">You're all caught up!</p>
            )}

            {/* Set up password — links to profile */}
            <div
              className="flex items-start gap-2 cursor-pointer hover:bg-gray-50 rounded-lg p-1 -m-1 transition-colors"
              onClick={() => navigate('/student/profile')}
            >
              <div className="w-5 h-5 rounded bg-brand-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3 h-3 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-700 leading-tight">Update your password</p>
                <p className="text-xs text-gray-400">Go to Profile</p>
              </div>
            </div>
          </div>
        </div>

        {/* Upcoming Holidays */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Upcoming Holidays</h3>
          <div className="space-y-2.5">
            {uniqueHolidays.length > 0 ? (
              uniqueHolidays.map((h, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${h.type === 'studio' ? 'bg-brand-500' : 'bg-orange-400'}`} />
                  <div>
                    <p className="text-sm text-gray-700">{h.name}</p>
                    <p className="text-xs text-gray-400">
                      {h.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      {h.type === 'studio' && ' — No class'}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-400">No upcoming holidays</p>
            )}
          </div>
          <button
            onClick={() => navigate('/student/calendar')}
            className="text-xs text-brand-500 hover:text-brand-600 font-medium mt-3"
          >
            View Calendar
          </button>
        </div>

        {/* Attendance Snapshot */}
        {totalClasses > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Attendance</h3>
            <div className="text-3xl font-bold text-brand-500 mb-1">{attendanceRate}%</div>
            <p className="text-xs text-gray-500 mb-3">{totalAttended} of {totalClasses} classes attended</p>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-brand-500 rounded-full h-2 transition-all"
                style={{ width: `${attendanceRate}%` }}
              />
            </div>
            {/* Streak */}
            {activeEnrollments.some(e => e.consecutiveAbsences === 0 && e.totalClassesAttended > 0) && (
              <p className="text-xs text-green-600 mt-2 font-medium">
                No recent absences — keep it up!
              </p>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default StudentOverview;
